import type { InspirationMediaType, StaffInspirationAsset } from '../../types/portalInspiration';
import { draftInspiration, fetchInspirationPreviewVideo, type DraftResponse } from './api';
import { captureForDraft } from './frameCapture';

// PORTAL INSPIRATION — the browser half of "Draft with AI": capture what the model will see, send it.
//
// ⚠️ ON-DEMAND ONLY. This is the ONLY function that calls the draft endpoint, and it is called from
// exactly two places: the edit dialog's Draft button, and the page's bulk "Draft with AI" loop (the same
// button pressed for several selected entries) plus the upload panel's "Draft text with AI after upload"
// tick - an explicit staff choice made before upload. Nothing here runs on load or on open
// (draftOnDemandOnly.test.ts pins it).

export async function draftEntry(input: {
  entryId: string;
  mediaType: InspirationMediaType;
  /** The file behind the entry (for its preview URLs), or null just after an upload. */
  asset: StaffInspirationAsset | null;
  /** The just-uploaded File, when still in memory (the best source). */
  file?: Blob | null;
  note?: string;
}): Promise<DraftResponse> {
  const { asset } = input;
  // ⚠️ A PHOTO IS READ BY THE SERVER from the processed rendition in storage: nothing is captured or sent from
  // the browser (a CDN with no CORS headers, or a HEIC named .jpg, used to make this fail with a decode error).
  if (input.mediaType === 'image') {
    return draftInspiration(input.entryId, {
      frames: [],
      durationSec: null,
      source: 'preview',
      note: input.note && input.note.trim() !== '' ? input.note.trim() : null,
    });
  }
  const captured = await captureForDraft({
    mediaType: input.mediaType,
    file: input.file ?? null,
    entryId: input.entryId,
    fetchPreviewVideo: fetchInspirationPreviewVideo,
    posterUrl: asset?.posterUrl ?? null,
    durationMs: asset?.durationMs ?? null,
  });
  return draftInspiration(input.entryId, {
    frames: captured.frames,
    durationSec: input.mediaType === 'video' ? captured.durationSec : null,
    source: captured.source,
    note: input.note && input.note.trim() !== '' ? input.note.trim() : null,
  });
}
